import React, { useEffect } from 'react';

import { Block, type Fragment, isBlockRangeSelection, TextRange } from '@gitbook/slate';
import type { List } from '@gitbook/util-immutable';
import { useInitialValue } from '@gitbook/util-react-hooks';
import { assert } from '@gitbook/util-ts';

import type { BlockProps, InlineProps } from '../interfaces';
import { getChildrenRanges, isOnlySelectedNode } from '../utils';
import { updateSelection, useEventHandlers } from './event-handlers';
import { FragmentRendererContext } from './FragmentRendererContext';
import NodeRenderer from './NodeRenderer';
import { NodeVirtualRenderer } from './NodeVirtualRenderer';
import { useVirtualizedNodeList } from './virtualization';

const DisabledFragmentEditingContext = React.createContext(false);

/**
 * Disable text editing in all fragment content nested under this component.
 *
 * It should be used when rendering multiple times the same fragment to avoid
 * selection issues because of Slate not being able to lookup reliably the right DOM elements.
 */
export function ToggleFragmentEditing(props: {
    readOnly: boolean;
    children: React.ReactNode;
}): React.ReactElement {
    const { readOnly, children } = props;

    return (
        <DisabledFragmentEditingContext.Provider value={readOnly}>
            {children}
        </DisabledFragmentEditingContext.Provider>
    );
}

/**
 * Component to render a fragment. Relies on a FragmentRendererContext.
 */
export function FragmentRenderer(props: {
    fragment: string;
    placeholder?: string;
    horizontalAlignment?: 'left' | 'center' | 'right';
}): React.ReactElement | null {
    const { fragment: fragmentProp, placeholder, horizontalAlignment } = props;
    const context = React.useContext(FragmentRendererContext);
    const fragmentReadOnly = React.useContext(DisabledFragmentEditingContext);

    if (!context) {
        throw new Error('No FragmentRendererContext found for FragmentRenderer');
    }

    const fragment = context.getFragment(fragmentProp);

    if (!fragment) {
        return null;
    }

    const fragmentProps = context.getFragmentRendererProps();

    const { isSelected: parentIsSelected, isFocused: parentIsFocused, editor } = fragmentProps;
    const { selection } = editor.value;

    // A fragment is selected if both its parent is selected and the selection has both the
    // anchor and focus in the fragment.
    const isSelected =
        parentIsSelected &&
        TextRange.isTextRange(selection) &&
        (selection.hasAnchorIn(fragment) || selection.hasFocusIn(fragment));

    const isFocused = parentIsFocused && isSelected;

    return (
        <MemoFragmentRenderer
            {...fragmentProps}
            fragmentReadOnly={fragmentReadOnly}
            isSelected={isSelected}
            containsBlockSelection={
                isBlockRangeSelection(selection) &&
                fragment.getBlocks().some((block) => fragment.hasNode(block.key))
            }
            isFocused={isFocused}
            fragment={fragment}
            placeholder={placeholder}
            horizontalAlignment={horizontalAlignment}
        />
    );
}

type MemoFragmentRendererProps = (BlockProps | InlineProps) & {
    containsBlockSelection: boolean;
    fragmentReadOnly: boolean;
    fragment: Fragment;
    valueDecorations: List<TextRange>;
    placeholder?: string;
    horizontalAlignment?: 'left' | 'center' | 'right';
};

/*
 * Lower-level, memo-ized component to render a fragment with all props present.
 *
 * This component is memoized to ignore selection and value changes (and possibly some
 * other stuff - see areEqual function below).
 * This means that if you need to listen to those changes, you should put that logic in the
 * higher-level component, which will re-render more often.
 */
const MemoFragmentRenderer = React.memo(function MemoFragmentRenderer(
    props: MemoFragmentRendererProps
): React.ReactElement {
    const {
        editor,
        isSelected,
        isFocused,
        node,
        parent,
        fragment,
        valueDecorations,
        ancestors,
        fragmentReadOnly,
        placeholder,
        horizontalAlignment,
    } = props;
    const { stack, value } = editor;
    const { selection } = value;

    const eventHandlerRef = React.useRef<HTMLDivElement | null>(null);
    const eventHandlers = useEventHandlers({
        domRef: eventHandlerRef,
        editor,
        fragmentEditor: true,
    });

    //
    // Decorations and selections
    //
    const allDecorations = valueDecorations
        .concat(stack.getDecorations(parent))
        .concat(stack.getDecorations(node));

    const filteredSelections = filterSelections(props);

    const [childrenDecorations, childrenSelections] = getChildrenRanges(
        fragment,
        allDecorations,
        filteredSelections
    );

    // @ts-expect-error
    const childrenAncestors = ancestors.push(node);

    const indexes = fragment.getSelectionIndexes(selection, isSelected);
    const readOnly = fragmentReadOnly || editor.readOnly;

    const virtualized = useVirtualizedNodeList({
        editor,
        nodeList: fragment.nodes.toArray(),
        parentRef: editor.elementRef,
        virtualizationEnabled: false,
        viewportOptions: { margin: '0px' },
    });

    const initialIsFocused = useInitialValue(isFocused);

    // When a fragment mount and is already focused we need to update the selection
    // because it means the selection is not up to date.
    useEffect(() => {
        if (initialIsFocused && editor.elementRef?.current) {
            updateSelection(editor.elementRef.current, editor);
        }
    }, [editor, initialIsFocused]);

    const children = virtualized.renderNodes(fragment, (child, vChild, index) => {
        assert(Block.isBlock(child), 'Top-level of fragment should only contain blocks');
        const isSelected = !!indexes && indexes.start <= index && index < indexes.end;

        return vChild.virtual ? (
            <NodeVirtualRenderer
                key={child.key}
                node={child}
                editor={editor}
                height={vChild.virtualHeight}
                domRef={vChild.ref}
            />
        ) : (
            <NodeRenderer
                block={null}
                editor={editor}
                decorations={childrenDecorations[index]}
                selections={childrenSelections[index]}
                isSelected={isSelected}
                isOnlyNodeSelected={isSelected && isOnlySelectedNode(selection, child, node)}
                isSelectedBlock={
                    isBlockRangeSelection(selection) && selection.hasBlock(child, fragment)
                }
                isBlockSelection={isBlockRangeSelection(selection)}
                isFocused={isFocused && isSelected}
                key={child.key}
                node={child}
                parent={fragment}
                readOnly={readOnly}
                ancestors={childrenAncestors}
                placeholder={placeholder}
                horizontalAlignment={horizontalAlignment}
                domRef={editor.virtualizeChildren ? vChild.ref : undefined}
            />
        );
    });

    return (
        <div
            ref={eventHandlerRef}
            data-key={fragment.key}
            data-fragment="true"
            data-slate-editor
            data-document-key={editor.value.document.key}
            contentEditable={readOnly ? undefined : true}
            suppressContentEditableWarning
            className="w-full"
            {...eventHandlers}
        >
            {children}
        </div>
    );
}, areEqual);

/**
 * Filter selections given a fragment.
 * TODO:
 * This is a fast fix to an ongoing issue with fragments and selections. However,
 * we should consider refactoring the information passed to Fragments in the case
 * where ALL fragments inside a table are selected for example (using CMD+A on
 * the document). Currently, it won't select anything in the table. Maybe we should
 * introduce a design concept for void blocks selections (such as the blue border
 * of void block like embeds, tables, or API blocks).
 * The fix would be to compute selections and include wether the selection is
 * included inside the component and restricted to it (one cell is selected), or if
 * the anchor and focus keys are OUTSIDE the fragments (whole portion of the document
 * is selected).
 */
function filterSelections(props: MemoFragmentRendererProps): List<TextRange> {
    const { selections, fragment } = props;

    return selections.filter((selection) =>
        fragment.nodes.some((node) =>
            selection.focusKey ? node.hasChild(selection.focusKey) : false
        )
    );
}

/*
 * Should the node avoid updating?
 */
function areEqual(props: MemoFragmentRendererProps, nextProps: MemoFragmentRendererProps): boolean {
    const n = nextProps;
    const p = props;

    // If the Editor reference has changed, update.
    if (n.editor !== p.editor) {
        return false;
    }

    // If the Fragment reference has changed, update.
    if (n.fragment !== p.fragment) {
        return false;
    }

    // If the selection value of the node or of some of its children has changed,
    // re-render in case there is any user-land logic depends on it to render.
    // if the node is selected update it, even if it was already selected: the
    // selection value of some of its children could have been changed and they
    // need to be rendered again.
    if (n.isSelected || p.isSelected) {
        return false;
    }
    if (n.isFocused || p.isFocused) {
        return false;
    }

    // If the mode of selection changes from block to text or vice versa, update.
    if (p.isBlockSelection !== n.isBlockSelection) {
        return false;
    }
    if (p.containsBlockSelection || n.containsBlockSelection) {
        return false;
    }

    // If the fragment has changed, update.
    if (
        n.node.fragments.get(n.fragment.fragmentKey) !==
        p.node.fragments.get(p.fragment.fragmentKey)
    ) {
        return false;
    }

    // If the selections have changed, update.
    if (!n.selections.equals(p.selections)) {
        return false;
    }

    // if the value decorations changed, update.
    if (!n.valueDecorations.equals(p.valueDecorations)) {
        return false;
    }

    // Otherwise, don't update.
    return true;
}
